import { getBaseURL, getClient } from '../BLL/clients/HttpClient';
import { ApiEndPoint } from '../BLL/Enums/ApiEndPoint';
import {
  Task,
  TaskDetail,
  TasksResponse,
  TaskFilters,
  CreateTaskInput,
  UpdateTaskInput,
  TaskTopic,
  CreateTopicInput,
  UpdateTopicInput,
  TaskFeedEntry,
  CreateFeedEntryInput,
  TaskTenantsListForNetworkResponse,
} from '../types/interfaces/task.types';

/**
 * Get all tasks with filters and pagination
 */
export const getTasks = async (
  filters: TaskFilters = {},
  page = 1,
  pageSize = 20,
  sortBy?: string,
  sortOrder: 'asc' | 'desc' = 'desc'
): Promise<TasksResponse> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const params: any = {
    page,
    pageSize,
    sortBy,
    sortOrder,
    ...filters,
  };

  // Convert arrays to comma-separated strings
  if (Array.isArray(filters.status)) {
    params.status = filters.status.join(',');
  }
  if (Array.isArray(filters.priority)) {
    params.priority = filters.priority.join(',');
  }

  const { data } = await client.get<TasksResponse>('/v1/tasks', { params });

  return data;
};

/**
 * Get a single task by ID
 */
export const getTaskById = async (
  taskId: string,
  includeFeed = false
): Promise<Task | TaskDetail> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.get<Task | TaskDetail>(`/v1/tasks/${taskId}`, {
    params: { includeFeed },
  });

  return data;
};

/**
 * Create a new task
 */
export const createTask = async (
  input: CreateTaskInput,
  userActor: 'CARRIER' | 'NETWORK' | 'FAIRFARE'
): Promise<Task> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const body: any = {
    title: input.title,
    description: input.additionalDescription,
    priority: input.taskPriority,
    topicId: input.topicId,
    // dueDate: input.dueDate, // Due date is not part of CreateTaskInput currently - when needed, add it back
    // we need to determine which case identifier to use - if the user is a carrier it will be case id, if it's a network it will be policy number
  };

  if (input.carrierTenantId && userActor !== 'CARRIER') {
    body['carrierTenantId'] = input.carrierTenantId;
  }

  if (userActor === 'CARRIER') {
    body['caseId'] = input.caseIdentifier;
  } else if (userActor === 'NETWORK') {
    body['policyNumber'] = input.caseIdentifier;
  } else if (userActor === 'FAIRFARE') {
    if (input.caseIdentifierType === 'caseId') {
      body['caseId'] = input.caseIdentifier;
    } else if (input.caseIdentifierType === 'policyNumber') {
      body['policyNumber'] = input.caseIdentifier;
    }
  }

  const { data } = await client.post<Task>('/v1/tasks', body);

  return data;
};

/**
 * Update an existing task
 */
export const updateTask = async (
  taskId: string,
  input: UpdateTaskInput
): Promise<Task> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.put<Task>(`/v1/tasks/${taskId}`, input);

  return data;
};

/**
 * Delete a task
 */
export const deleteTask = async (taskId: string): Promise<void> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  await client.delete(`/v1/tasks/${taskId}`);
};

/**
 * Get task feed
 */
export const getTaskFeed = async (taskId: string): Promise<TaskFeedEntry[]> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.get<TaskFeedEntry[]>(
    `/v1/tasks/${taskId}/feed`
  );

  return data;
};

/**
 * Add a feed entry (comment or attachment)
 */
export const addFeedEntry = async (
  taskId: string,
  input: CreateFeedEntryInput
): Promise<TaskFeedEntry> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.post<TaskFeedEntry>(
    `/v1/tasks/${taskId}/feed`,
    input
  );

  return data;
};

/**
 * Get topics available for current user
 */
export const getTopics = async (): Promise<TaskTopic[]> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.get<TaskTopic[]>('/v1/tasks/topics');

  return data;
};

/**
 * Get all topics (admin only)
 */
export const getAllTopics = async (): Promise<TaskTopic[]> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.get<TaskTopic[]>('/v1/tasks/topics/all');

  return data;
};

/**
 * Get a specific topic by ID
 */
export const getTopicById = async (topicId: number): Promise<TaskTopic> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.get<TaskTopic>(`/v1/tasks/topics/${topicId}`);

  return data;
};

/**
 * Create a new topic (admin only)
 */
export const createTopic = async (
  input: CreateTopicInput
): Promise<TaskTopic> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.post<TaskTopic>('/v1/tasks/topics', input);

  return data;
};

/**
 * Update a topic (admin only)
 */
export const updateTopic = async (
  topicId: number,
  input: UpdateTopicInput
): Promise<TaskTopic> => {
  const baseUrl = getBaseURL(ApiEndPoint.Auth);
  const client = getClient({ baseURL: baseUrl });

  const { data } = await client.put<TaskTopic>(
    `/v1/tasks/topics/${topicId}`,
    input
  );

  return data;
};

/**
 * Get tenants available for network and fairfare users
 */
export const getTenants =
  async (): Promise<TaskTenantsListForNetworkResponse> => {
    const baseUrl = getBaseURL(ApiEndPoint.Auth);
    const client = getClient({ baseURL: baseUrl });

    const { data } = await client.get<TaskTenantsListForNetworkResponse>(
      '/v1/tenants'
    );

    return data;
  };
